import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';

import { Keyboard, Navigation, Pagination, Zoom } from 'swiper/modules';
import { Link, useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { idFromSlug, slugify } from '../utils/slug';
import useSeo, { absoluteUrl, toPlainText } from '../seo/useSeo';

import API_BASE from '../config';
import ProductService from '../services/product.service';
import Spinner from './Spinner';
import { formatCurrency } from '../common/utils';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import { useCart } from '../context/CartContext';
import { useTranslation } from 'react-i18next';

const resolveImg = (val) => {
  if (!val) return '';
  if (/^https?:\/\//i.test(val)) return val;
  const clean = val.replace(/^uploads\//, '/');
  return API_BASE + (clean.startsWith('/') ? clean : '/' + clean);
};

const ProductDetail = () => {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const [qty, setQty] = useState(1);
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Fullscreen lightbox: index of the opened slide, or null when closed.
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const lightboxSwiper = useRef(null);

  // Slides = cover image (product.image_url) first, then extra images in order.
  const slides = useMemo(() => {
    if (!product) return [];
    const out = [];
    if (product.image_url) out.push({ key: 'cover', src: resolveImg(product.image_url), caption: '' });
    (product.images || []).forEach((img) => out.push({ key: img.id, src: resolveImg(img.url), caption: img.caption || '' }));
    return out;
  }, [product]);

  // Lock page scroll + close on Escape while the lightbox is open (same as Gallery).
  useEffect(() => {
    if (lightboxIndex === null) return undefined;
    const body = document.body;
    const scrollY = window.scrollY;
    const prev = { overflow: body.style.overflow, position: body.style.position, top: body.style.top, width: body.style.width };
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIndex(null);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      Object.assign(body.style, prev);
      window.scrollTo(0, scrollY);
      window.removeEventListener('keydown', onKey);
    };
  }, [lightboxIndex]);

  const zoomIn = () => lightboxSwiper.current && lightboxSwiper.current.zoom.in();
  const zoomOut = () => lightboxSwiper.current && lightboxSwiper.current.zoom.out();

  useEffect(() => {
    const id = idFromSlug(slug);
    if (!id) {
      setError(t('product.invalidUrl'));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    setLightboxIndex(null);
    ProductService.get(id)
      .then((res) => {
        setProduct(res.data);
        // Normalize URL to the canonical slug if it drifted (e.g. renamed product).
        const canonical = slugify(res.data.name, res.data.id);
        if (canonical !== slug) {
          navigate(`/product/${canonical}`, { replace: true });
        }
      })
      .catch((err) => {
        const msg = (err.response && err.response.data && err.response.data.message) || err.message || t('product.loadError');
        setError(err.response && err.response.status === 404 ? t('product.notFound') : msg);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, navigate]);

  const productUrl = product ? `/product/${slugify(product.name, product.id)}` : null;
  const productImage = product && (product.image_url || (product.images && product.images[0] && product.images[0].url));
  useSeo(
    product
      ? {
          title: product.name,
          description: toPlainText(product.description) || product.name,
          image: productImage ? resolveImg(productImage) : undefined,
          type: 'product',
          canonical: productUrl,
          jsonLd: [
            {
              '@context': 'https://schema.org',
              '@type': 'Product',
              name: product.name,
              description: toPlainText(product.description, 500),
              image: slides.map((sl) => absoluteUrl(sl.src)),
              url: absoluteUrl(productUrl),
              category: product.catalog ? product.catalog.name : undefined,
              offers: {
                '@type': 'Offer',
                price: Number(product.price || 0).toFixed(2),
                priceCurrency: process.env.REACT_APP_CURRENCY || 'VND',
                availability: 'https://schema.org/InStock',
                url: absoluteUrl(productUrl),
              },
            },
            {
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: t('nav.catalog'), item: absoluteUrl('/catalog') },
                { '@type': 'ListItem', position: 2, name: product.name, item: absoluteUrl(productUrl) },
              ],
            },
          ],
        }
      : error
        ? { title: t('product.notFound'), noindex: true }
        : null,
  );

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return (
      <div className='container mt-4'>
        <div className='alert alert-danger'>{error}</div>
        <Link to='/catalog' className='btn btn-secondary'>
          {t('common.backToCatalog')}
        </Link>
      </div>
    );
  }

  return (
    <div className='container mt-4'>
      <nav aria-label='breadcrumb'>
        <ol className='breadcrumb'>
          <li className='breadcrumb-item'>
            <Link to='/catalog'>{t('nav.catalog')}</Link>
          </li>
          {product.catalog && <li className='breadcrumb-item'>{product.catalog.name}</li>}
          {product.sub_catalog && <li className='breadcrumb-item'>{product.sub_catalog.name}</li>}
          <li className='breadcrumb-item active' aria-current='page'>
            {product.name}
          </li>
        </ol>
      </nav>

      <div className='row'>
        <div className='col-md-6 mb-3'>
          {slides.length > 0 ? (
            <>
              <Swiper modules={[Navigation, Pagination, Zoom, Keyboard]} spaceBetween={16} slidesPerView={1} navigation={slides.length > 1} keyboard={{ enabled: true }} pagination={slides.length > 1 ? { clickable: true } : false} zoom={{ maxRatio: 3, toggle: true }} loop={slides.length > 1} className='album-swiper rounded'>
                {slides.map((sl, idx) => (
                  <SwiperSlide key={sl.key}>
                    <div className='album-slide'>
                      {/* Double-click / pinch to zoom inline */}
                      <div className='swiper-zoom-container album-slide__zoom' style={{ height: 420 }}>
                        <img src={sl.src} alt={sl.caption || product.name} style={{ maxHeight: 420 }} />
                      </div>
                      <button type='button' className='album-slide__expand' onClick={() => setLightboxIndex(idx)} title={t('album.fullscreen', 'View fullscreen')} aria-label={t('album.fullscreen', 'View fullscreen')}>
                        ⤢
                      </button>
                      {(sl.caption || slides.length > 1) && (
                        <div className='album-slide__caption'>
                          {slides.length > 1 && (
                            <span className='album-slide__counter'>
                              {idx + 1} / {slides.length}
                            </span>
                          )}
                          {sl.caption && <div className='album-slide__title'>{sl.caption}</div>}
                        </div>
                      )}
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {slides.length > 1 && (
                <div className='album-thumbs d-flex flex-wrap gap-2 justify-content-center mt-2'>
                  {slides.map((sl, idx) => (
                    <img key={sl.key} src={sl.src} alt={sl.caption || ''} title={sl.caption || ''} onClick={() => setLightboxIndex(idx)} />
                  ))}
                </div>
              )}

              {lightboxIndex !== null && (
                <div className='album-lightbox' role='dialog' aria-modal='true'>
                  <div className='album-lightbox__toolbar'>
                    <button type='button' onClick={zoomIn} title={t('album.zoomIn', 'Zoom in')}>
                      ＋
                    </button>
                    <button type='button' onClick={zoomOut} title={t('album.zoomOut', 'Zoom out')}>
                      －
                    </button>
                    <button type='button' onClick={() => setLightboxIndex(null)} title={t('album.close', 'Close')}>
                      ✕
                    </button>
                  </div>
                  <Swiper modules={[Navigation, Pagination, Zoom, Keyboard]} initialSlide={lightboxIndex} onSwiper={(sw) => (lightboxSwiper.current = sw)} navigation keyboard={{ enabled: true }} pagination={{ type: 'fraction' }} zoom={{ maxRatio: 5, toggle: true }} className='album-lightbox__swiper'>
                    {slides.map((sl) => (
                      <SwiperSlide key={sl.key}>
                        <div className='swiper-zoom-container'>
                          <img src={sl.src} alt={sl.caption || product.name} />
                        </div>
                        <div className='album-lightbox__caption'>
                          <div className='album-slide__title'>{sl.caption || product.name}</div>
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>
                </div>
              )}
            </>
          ) : (
            <div className='bg-light d-flex align-items-center justify-content-center text-muted rounded border' style={{ height: 320 }}>
              {t('common.noImage')}
            </div>
          )}
        </div>

        <div className='col-md-6'>
          <h2>{product.name}</h2>
          <h3 className='text-primary mb-3'>{formatCurrency(product.price)}</h3>

          <table className='table table-hover'>
            <tbody>
              <tr>
                <th style={{ width: 140 }}>{t('product.catalog')}</th>
                <td>{product.catalog ? product.catalog.name : product.catalog_id}</td>
              </tr>
              <tr>
                <th>{t('product.subCatalog')}</th>
                <td>{product.sub_catalog ? product.sub_catalog.name : product.sub_catalog_id}</td>
              </tr>
              <tr>
                <th>{t('product.productId')}</th>
                <td>{product.id}</td>
              </tr>
            </tbody>
          </table>

          <Link to='/catalog' className='btn btn-outline-secondary mt-2'>
            {t('common.backToCatalog')}
          </Link>

          <div className='d-flex align-items-center gap-2 mt-3'>
            <input
              type='number'
              min='1'
              className='form-control'
              style={{ width: 90 }}
              value={qty}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                setQty(Number.isInteger(v) && v >= 1 ? v : 1);
              }}
            />
            <button type='button' className='btn btn-primary' onClick={() => addToCart(product.id, qty).catch(() => {})}>
              {t('cart.addToCart')}
            </button>
          </div>
        </div>
      </div>
      {product.description && (
        <div className='row mt-4'>
          <h5>{t('product.detailDescription')}</h5>
          <div className='html-preview mx-2' dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.description) }} />
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
